import React from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import {
  COMPETENCIES,
  TIER_MODELS,
  scoreColor,
  scoreOf,
  tierAverage,
  readinessPct,
} from "@/data/coachingData";
import { CompetencyRadarChart } from "@/components/CompetencyRadarChart";
import { DimensionSummary } from "@/components/DimensionSummary";
import { CohortTable } from "@/components/CohortTable";
import { ThreeSixtyView } from "@/components/ThreeSixtyView";
import { ProgramTimelineGantt } from "@/components/ProgramTimelineGantt";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { TrendingUp, Target, AlertTriangle, Gauge } from "lucide-react";

function Kpi({ icon: Icon, label, value, sub, accent, testid, compact = false }) {
  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Card className="relative overflow-hidden bg-white border-slate-200 p-5" data-testid={testid}>
        <span className="absolute top-0 left-0 w-full h-1" style={{ background: accent }} />
        <div className="flex items-center gap-2 text-slate-600 mb-2">
          <Icon size={16} style={{ color: accent }} />
          <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
        </div>
        <p
          className={`font-heading font-extrabold text-slate-900 tabular-nums ${
            compact ? "text-lg leading-tight line-clamp-2" : "text-3xl"
          }`}
        >
          {value}
        </p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </Card>
    </motion.div>
  );
}

export function Dashboard({ modelType, scores, teamScores = {}, headcounts = {}, recommendations }) {
  const { t } = useI18n();
  const tiers = TIER_MODELS[modelType];

  const readinessData = tiers.map((tier) => {
    const avg = tierAverage(scores, tier.key);
    return { name: t(`tierShort.${tier.key}`), readiness: readinessPct(avg), avg };
  });

  const orgAvg = readinessData.reduce((a, r) => a + r.avg, 0) / readinessData.length;
  const orgReadiness = recommendations?.org_readiness_score ?? readinessPct(orgAvg);

  const compAverages = COMPETENCIES.map((c) => {
    const avg = tiers.reduce((a, tier) => a + scoreOf(scores, tier.key, c.key), 0) / tiers.length;
    return { label: t(`comp.${c.key}`), avg };
  }).sort((a, b) => a.avg - b.avg);
  const topGap = recommendations?.top_gaps?.[0] || compAverages[0]?.label;

  const priorityPrograms = recommendations?.programs?.filter((p) => p.priority === "High").length || 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          icon={Gauge}
          label={t("dash.kpiReadiness")}
          value={`${orgReadiness}%`}
          sub={t("dash.kpiReadinessSub")}
          accent="#6366F1"
          testid="kpi-org-readiness"
        />
        <Kpi
          icon={AlertTriangle}
          label={t("dash.kpiTopGap")}
          value={topGap}
          sub={t("dash.kpiTopGapSub")}
          accent="#F43F5E"
          testid="kpi-top-gap"
          compact
        />
        <Kpi
          icon={Target}
          label={t("dash.kpiLevels")}
          value={tiers.length}
          sub={t("dash.kpiLevelsSub", { model: t(`model.${modelType}`) })}
          accent="#F59E0B"
          testid="kpi-levels-assessed"
        />
        <Kpi
          icon={TrendingUp}
          label={t("dash.kpiHigh")}
          value={priorityPrograms}
          sub={recommendations ? t("dash.kpiHighSub") : t("dash.kpiHighEmpty")}
          accent="#10B981"
          testid="kpi-high-priority"
        />
      </div>

      <div>
        <div className="mb-3">
          <h3 className="font-heading text-base md:text-lg font-semibold text-slate-900">
            {t("dim.summaryTitle")}
          </h3>
          <p className="text-xs text-slate-500">{t("dim.summarySub")}</p>
        </div>
        <DimensionSummary tiers={tiers} scores={scores} insight={recommendations?.dimension_insight} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200 p-5">
          <h3 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-1">
            {t("dash.radarTitle")}
          </h3>
          <p className="text-xs text-slate-500 mb-4">{t("dash.radarSub")}</p>
          <CompetencyRadarChart modelType={modelType} scores={scores} />
        </Card>

        <Card className="bg-white border-slate-200 p-5">
          <h3 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-1">
            {t("dash.readinessTitle")}
          </h3>
          <p className="text-xs text-slate-500 mb-4">{t("dash.readinessSub")}</p>
          <div className="h-[360px] w-full" data-testid="readiness-bar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={readinessData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={{ stroke: "#CBD5E1" }} />
                <YAxis domain={[0, 100]} tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={{ stroke: "#CBD5E1" }} />
                <Tooltip
                  cursor={{ fill: "#ffffff08" }}
                  contentStyle={{ background: "#ffffff", border: "1px solid #CBD5E1", borderRadius: 12, color: "#0F172A", fontSize: 12 }}
                />
                <Bar dataKey="readiness" radius={[6, 6, 0, 0]} maxBarSize={64}>
                  {readinessData.map((d, i) => (
                    <Cell key={i} fill={scoreColor(d.avg)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="bg-white border-slate-200 p-5">
        <h3 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-1">
          {t("cohort.title")}
        </h3>
        <p className="text-xs text-slate-500 mb-4">{t("cohort.sub")}</p>
        <CohortTable tiers={tiers} scores={scores} headcounts={headcounts} />
      </Card>

      {Object.keys(teamScores).length > 0 && (
        <Card className="bg-white border-slate-200 p-5">
          <h3 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-1">
            {t("t360.title")}
          </h3>
          <p className="text-xs text-slate-500 mb-4">{t("t360.sub")}</p>
          <ThreeSixtyView tiers={tiers} scores={scores} teamScores={teamScores} />
        </Card>
      )}

      <Card className="bg-white border-slate-200 p-5">
        <h3 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-1">
          {t("dash.timelineTitle")}
        </h3>
        <p className="text-xs text-slate-500 mb-4">{t("dash.timelineSub")}</p>
        {recommendations?.programs ? (
          <ProgramTimelineGantt programs={recommendations.programs} />
        ) : (
          <div className="py-12 text-center text-slate-500 text-sm">{t("dash.timelineEmpty")}</div>
        )}
      </Card>
    </div>
  );
}
